<template>
	<m-pageframe title="RedChirp App Store">
		<v-container class="mb-10">
			<v-row class="mt-4">
				<v-col>
					<h1 class="text-h4 font-weight-bold mb-1">Welcome to the <span class="primary--text">RedChirp App
							Store</span>
					</h1>
					<p class="text-h6 text--secondary font-weight-regular">
						Discover and install powerful add-ons to enhance your RedChirp experience
					</p>
				</v-col>
			</v-row>

			<v-row
				justify="space-between"
				align="end"
			>
				<v-col>
					<v-tabs
						v-model="active_tab_index"
						class="mb-4"
						show-arrows
					>
						<v-tab key="available">Available Apps ({{ available_apps.length }})</v-tab>
						<v-tab key="installed">Installed Apps ({{ installed_apps.length }})</v-tab>
					</v-tabs>
				</v-col>

				<v-col
					cols="12"
					sm="6"
					md="4"
					lg="3"
				>
					<v-text-field
						autocomplete="off"
						:label="`Search ${active_tab === 'available' ? 'Available' : 'Installed'} Apps`"
						prepend-icon="mdi-magnify"
						v-model="full_text"
						@input="onSearch"
					/>
				</v-col>
			</v-row>

			<v-tabs-items v-model="active_tab_index">
				<v-tab-item key="available">
					<app-skeleton-grid
						:show_loading="mixin_show_loading"
						:skeleton_count="page_size"
					>
						<empty-state
							v-if="!mixin_loading && available_apps.length === 0"
							v-bind="available_empty_state"
						>
							<template #actions>
								<v-btn
									v-if="full_text"
									color="primary"
									outlined
									@click="clearSearch"
								>
									Clear Search
								</v-btn>
							</template>
						</empty-state>

						<div v-else>
							<v-row
								align="stretch"
								class="mt-0 mb-5"
							>
								<v-col
									v-for="(item, index) in paginated_available_apps"
									:key="index"
									cols="12"
									md="6"
									lg="4"
								>
									<app-search-card
										:app="item"
										@installed="goToAppDetails"
										@uninstalled="onUninstall"
									/>
								</v-col>
							</v-row>

							<v-row
								justify="center"
								class="mt-10 mb-5"
								v-if="available_apps_pages > 1"
							>
								<v-pagination
									v-model="available_apps_page"
									:length="available_apps_pages"
									:total-visible="7"
									@input="onPageChange"
								/>
							</v-row>
						</div>
					</app-skeleton-grid>
				</v-tab-item>

				<v-tab-item key="installed">
					<app-skeleton-grid
						:show_loading="mixin_show_loading"
						:skeleton_count="page_size"
					>
						<empty-state
							v-if="!mixin_loading && installed_apps.length === 0"
							v-bind="installed_empty_state"
						>
							<template #actions>
								<v-btn
									v-if="full_text"
									color="primary"
									outlined
									@click="clearSearch"
									class="mr-2"
								>
									Clear Search
								</v-btn>

								<v-btn
									v-if="!full_text"
									color="primary"
									@click="active_tab_index = 0"
								>
									Browse Available Apps
								</v-btn>
							</template>
						</empty-state>

						<div v-else>
							<v-row
								align="stretch"
								class="mt-0 mb-5"
							>
								<v-col
									v-for="(item, index) in paginated_installed_apps"
									:key="index"
									cols="12"
									sm="6"
									md="4"
								>
									<app-search-card
										:app="item"
										@installed="goToAppDetails"
										@uninstalled="onUninstall"
									/>
								</v-col>
							</v-row>

							<v-row
								justify="center"
								class="mt-10 mb-5"
								v-if="installed_apps_pages > 1"
							>
								<v-pagination
									v-model="installed_apps_page"
									:length="installed_apps_pages"
									:total-visible="7"
									@input="onPageChange"
								/>
							</v-row>
						</div>
					</app-skeleton-grid>
				</v-tab-item>
			</v-tabs-items>
		</v-container>
	</m-pageframe>
</template>

<script>
import _ from 'lodash';
import AppSearchCard from '@/components/app-store/app-search-card.vue';
import AppSkeletonGrid from '@/components/app-store/app-skeleton-grid.vue';
import AppInstallationMixin from '@/mixins/app-installation.js';
import DelayedLoadingMixin from '@/mixins/delayed-loading.js';
import UrlTabSync from '../../mixins/url-tab-sync.js'; // relative import for JSDoc

export default {
	mixins: [DelayedLoadingMixin, AppInstallationMixin, UrlTabSync],

	components: {
		AppSearchCard,
		AppSkeletonGrid,
		EmptyState: () => import('@/components/global/empty-state.vue'),
	},

	data() {
		return {
			full_text: null,
			available_apps_page: 1,
			installed_apps_page: 1,
			page_size: 6,
			mixin_tab_config: {
				tab_mapping: {
					available: 0,
					installed: 1
				}
			}
		};
	},

	computed: {
		active_tab() {
			return this.active_tab_index === 0 ? 'available' : 'installed';
		},

		available_empty_state() {
			return {
				icon: 'mdi-package-variant',
				title: this.full_text ? 'No apps found' : 'No apps available',
				description: this.full_text
					? `No apps match your search for "${this.full_text}". Try a different search term.`
					: 'There are currently no apps available in the store.',
			};
		},

		installed_empty_state() {
			return {
				icon: 'mdi-download-off',
				title: this.full_text ? 'No installed apps found' : 'No apps installed',
				description: this.full_text
					? `No installed apps match your search for "${this.full_text}". Try a different search term.`
					: "You haven't installed any apps yet. Browse the available apps to get started.",
			};
		},

		available_apps() {
			return this.mixin_all_published_apps.filter(app => app.installed_version == null);
		},

		installed_apps() {
			return this.mixin_all_published_apps.filter(app => app.installed_version != null);
		},

		available_apps_pages() {
			return Math.ceil(this.available_apps.length / this.page_size);
		},

		installed_apps_pages() {
			return Math.ceil(this.installed_apps.length / this.page_size);
		},

		paginated_available_apps() {
			const start = (this.available_apps_page - 1) * this.page_size;
			const end = start + this.page_size;
			return this.available_apps.slice(start, end);
		},

		paginated_installed_apps() {
			const start = (this.installed_apps_page - 1) * this.page_size;
			const end = start + this.page_size;
			return this.installed_apps.slice(start, end);
		},
	},

	mounted() {
		this.doUpdate();
	},

	methods: {
		clearSearch() {
			this.full_text = null;
			this.doUpdate();
		},

		onSearch: _.debounce(function () {
			this.available_apps_page = 1;
			this.installed_apps_page = 1;
			this.doUpdate();
		}, 751),

		onPageChange() {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},

		onUninstall() {
			this.doUpdate();
			this.active_tab_index = 0;
		},

		doUpdate() {
			this.mixinStartDelayedLoading();
			this.mixinGetAllAvailableApps(this.mixinStopDelayedLoading);
		},

		goToAppDetails(app) {
			this.$router.push({
				name: 'app-details',
				params: { organization_id: this.$rc.getOrganizationId(), app_id: app.app_id },
			});
		}
	}
}
</script>

<style lang="scss" scoped>
.container {
	max-width: 84rem;
}
</style>